import { Injectable, inject, NgZone, OnDestroy } from '@angular/core';
import { SettingsService, _HttpClient } from '@delon/theme';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { QuinnyCampaignComponent } from '../components/quinny-campaign/quinny-campaign.component';
import { QuinnyN3RedirectService } from './quinny-n3-redirect.service';

export interface QuinnyCampaignState {
  enabled: boolean;
  dismissCount: number;
  completed: boolean;
  lastDismissDate?: string | null;
}

const CAMPAIGN_SYNC_KEY = 'quinny_campaign_sync';

@Injectable({
  providedIn: 'root'
})
export class QuinnyCampaignService implements OnDestroy {
  private http = inject(_HttpClient);
  private settings = inject(SettingsService);
  private modalSrv = inject(NzModalService);
  private ngZone = inject(NgZone);
  private quinnyN3Redirect = inject(QuinnyN3RedirectService);

  private isModalShowing = false;
  private isDismissing = false;
  private activeModalRef: NzModalRef | null = null;
  private storageListener: ((event: StorageEvent) => void) | null = null;

  constructor() {
    this.initCrossTabSync();
  }

  ngOnDestroy(): void {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
  }

  private initCrossTabSync(): void {
    this.storageListener = (event: StorageEvent) => {
      if (event.key === CAMPAIGN_SYNC_KEY && event.newValue) {
        this.ngZone.run(() => {
          try {
            const syncData = JSON.parse(event.newValue!);
            this.updateLocalCampaignState(syncData);
            if (this.activeModalRef && !this.shouldShowCampaign()) {
              this.activeModalRef.close({ action: 'synced' });
            }
          } catch {
            // Ignore parse errors
          }
        });
      }
    };
    window.addEventListener('storage', this.storageListener);
  }

  private broadcastCampaignUpdate(state: Partial<QuinnyCampaignState>): void {
    try {
      localStorage.setItem(CAMPAIGN_SYNC_KEY, JSON.stringify(state));
      localStorage.removeItem(CAMPAIGN_SYNC_KEY);
    } catch {
      // localStorage might be unavailable
    }
  }

  shouldShowCampaign(): boolean {
    const campaign = this.getCampaignState();

    if (!campaign) {
      return false;
    }

    if (campaign.enabled !== true || campaign.completed === true) {
      return false;
    }

    if (campaign.dismissCount >= 3) {
      return false;
    }

    if (campaign.lastDismissDate) {
      const todayUtc = this.getTodayUtcString();
      if (campaign.lastDismissDate === todayUtc) {
        return false;
      }
    }

    return true;
  }

  getCampaignState(): QuinnyCampaignState | null {
    const user = this.settings.user;
    return user?.['quinnyCampaign'] ?? null;
  }

  showCampaignModal(): void {
    if (this.isModalShowing) {
      return;
    }

    if (!this.shouldShowCampaign()) {
      return;
    }

    this.isModalShowing = true;

    this.activeModalRef = this.modalSrv.create({
      nzTitle: undefined,
      nzContent: QuinnyCampaignComponent,
      nzWidth: '960px',
      nzWrapClassName: 'quinny-campaign-modal',
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzFooter: null
    });

    this.activeModalRef.afterClose.subscribe((result: { action: string } | undefined) => {
      this.isModalShowing = false;
      this.activeModalRef = null;

      if (result?.action === 'try-now') {
        this.markTryNow();
      } else if (result?.action === 'dismiss') {
        this.markDismissed();
      }
    });
  }

  private async markTryNow(): Promise<void> {
    if (await this.quinnyN3Redirect.shouldRedirectToN3()) {
      await this.quinnyN3Redirect.openOrNavigate('/ai/copilot', {
        openDemo: true
      });
    } else {
      await this.quinnyN3Redirect.openOrNavigate('/ai/copilot');
    }

    const stateUpdate = { completed: true };
    this.updateLocalCampaignState(stateUpdate);
    this.broadcastCampaignUpdate(stateUpdate);

    this.http.post('api/quinny-campaign/try-now?_silent=true', {}).subscribe({
      error: err => {
        console.error('Failed to mark quinny campaign try-now:', err);
      }
    });
  }

  private markDismissed(): void {
    if (this.isDismissing) {
      return;
    }

    this.isDismissing = true;

    const currentState = this.getCampaignState();
    const newDismissCount = (currentState?.dismissCount ?? 0) + 1;
    const lastDismissDate = this.getTodayUtcString();

    const stateUpdate = { dismissCount: newDismissCount, lastDismissDate };
    this.updateLocalCampaignState(stateUpdate);
    this.broadcastCampaignUpdate(stateUpdate);

    this.http.post('api/quinny-campaign/dismiss?_silent=true', {}).subscribe({
      next: (res: any) => {
        this.isDismissing = false;
        if (res?.dismissCount != null) {
          const serverState = {
            dismissCount: res.dismissCount,
            lastDismissDate: res.lastDismissDate ?? lastDismissDate
          };
          this.updateLocalCampaignState(serverState);
          this.broadcastCampaignUpdate(serverState);
        }
      },
      error: err => {
        this.isDismissing = false;
        console.error('Failed to mark quinny campaign dismissed:', err);
      }
    });
  }

  private getTodayUtcString(): string {
    const now = new Date();
    return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
  }

  private updateLocalCampaignState(
    updates: Partial<QuinnyCampaignState>
  ): void {
    const user = this.settings.user;
    const currentCampaign = user?.['quinnyCampaign'] ?? {};

    this.settings.setUser({
      ...user,
      quinnyCampaign: {
        ...currentCampaign,
        ...updates
      }
    });
  }
}
